import { useState, useMemo } from 'react'
import { useQuery, useMutation } from 'convex/react'
import { Link } from 'react-router-dom'
import { Loader2, Pencil, Check, X, Grid3X3, ChevronDown, Users, Mic } from 'lucide-react'
import { api } from '../../../../convex/_generated/api'
import { Id } from '../../../../convex/_generated/dataModel'
import { useAuth } from '@/contexts/auth-context'
import { cn } from '@/lib/utils'

const MODALITY_COLORS: Record<string, { bg: string; text: string; border: string; active: string }> = {
  'text-only': { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', active: 'bg-blue-600 text-white border-blue-600' },
  'text-with-reference-photos': { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200', active: 'bg-violet-600 text-white border-violet-600' },
  'image-to-video': { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', active: 'bg-amber-600 text-white border-amber-600' },
  'existing-video-remix': { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', active: 'bg-emerald-600 text-white border-emerald-600' },
  'avatar-talking-head': { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200', active: 'bg-rose-600 text-white border-rose-600' },
  'screen-recording': { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200', active: 'bg-cyan-600 text-white border-cyan-600' },
}

const USE_CASE_COLORS: Record<string, string> = {
  'marketing': 'bg-orange-500',
  'entertainment': 'bg-pink-500',
  'education': 'bg-indigo-500',
  'social-media': 'bg-sky-500',
  'personal': 'bg-teal-500',
  'storytelling': 'bg-purple-500',
  'product-demo': 'bg-lime-600',
}

const SKILL_LABELS: Record<string, string> = {
  'text-to-video': 'T2V',
  'image-to-video': 'I2V',
  'reference-to-video': 'Ref',
  'first-last-frame': 'FLF',
  'edit-video': 'Edit',
  'video-reference': 'VRef',
  'avatar': 'Avt',
  'multi-shot': 'Multi',
  'uploaded': 'Upl',
  'extend-video': 'Ext',
  'characters': 'Chars',
  'voiceover': 'VO',
  'tts': 'TTS',
  'desktop': 'Desk',
}

function modalityClasses(slug: string, isActive: boolean) {
  const c = MODALITY_COLORS[slug]
  if (!c) return isActive ? 'bg-neutral-600 text-white border-neutral-600' : 'bg-neutral-50 text-neutral-600 border-neutral-200'
  return isActive ? c.active : `${c.bg} ${c.text} ${c.border}`
}

function useCaseColor(slug: string) {
  return USE_CASE_COLORS[slug] || 'bg-neutral-400'
}

function formatTimeAgo(ts: number): string {
  const diff = Date.now() - ts
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  if (hours < 24) return `${hours}h ago`
  if (days < 7) return `${days}d ago`
  return new Date(ts).toLocaleDateString()
}

function Sparkline({ data, color = 'currentColor', width = 64, height = 20 }: { data: number[]; color?: string; width?: number; height?: number }) {
  if (!data.length || data.every((d) => d === 0)) return <div style={{ width, height }} className="opacity-20 flex items-end justify-center text-[9px] text-neutral-400">—</div>
  const max = Math.max(...data, 1)
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width
    const y = height - (v / max) * height
    return `${x},${y}`
  }).join(' ')
  return (
    <svg width={width} height={height} className="overflow-visible">
      <polyline points={points} fill="none" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
      {data[data.length - 1] > 0 && (
        <circle cx={width} cy={height - (data[data.length - 1] / max) * height} r={2} fill={color} />
      )}
    </svg>
  )
}

function SkillHeatmap({ skills, features }: { skills: Record<string, number>; features: Record<string, number> }) {
  const all = { ...skills, ...features }
  const entries = Object.entries(all).sort((a, b) => b[1] - a[1])
  if (entries.length === 0) return null
  const max = Math.max(...entries.map(([, v]) => v), 1)

  return (
    <div className="flex flex-wrap gap-0.5">
      {entries.map(([key, count]) => {
        const intensity = count / max
        const bg = intensity > 0.7 ? 'bg-indigo-600 text-white' : intensity > 0.3 ? 'bg-indigo-200 text-indigo-800' : 'bg-indigo-50 text-indigo-600'
        return (
          <span key={key} className={cn('text-[9px] font-mono px-1 py-0.5 rounded', bg)} title={`${key}: ${count}`}>
            {SKILL_LABELS[key] || key}
          </span>
        )
      })}
    </div>
  )
}

export default function AdminClustersPage() {
  const { user, isAuthenticated } = useAuth()
  const isAdmin = isAuthenticated && user?.isAdmin

  const clusters = useQuery(api.clustering.listClustersAdmin, isAdmin ? undefined : 'skip')
  const stats = useQuery(api.clustering.getClusteringStats, isAdmin ? undefined : 'skip')
  const runs = useQuery(api.clustering.listClusteringRuns, isAdmin ? undefined : 'skip')
  const updateCluster = useMutation(api.clustering.updateClusterDescription)

  const [expandedId, setExpandedId] = useState<Id<'projectClusters'> | null>(null)
  const [editingId, setEditingId] = useState<Id<'projectClusters'> | null>(null)
  const [editDesc, setEditDesc] = useState('')
  const [filterModality, setFilterModality] = useState<string | null>(null)
  const [filterUseCase, setFilterUseCase] = useState<string | null>(null)
  const [eligibility, setEligibility] = useState<'all' | 'clips' | 'exports'>('all')

  const uniqueModalities = useMemo(() => clusters ? [...new Set(clusters.map((c) => c.inputModality))].sort() : [], [clusters])
  const uniqueUseCases = useMemo(() => clusters ? [...new Set(clusters.map((c) => c.useCase))].sort() : [], [clusters])

  const eligibilityCount = (c: any) =>
    eligibility === 'clips' ? (c.withClips ?? 0)
    : eligibility === 'exports' ? (c.withExports ?? 0)
    : c.projectCount

  const filtered = useMemo(() => {
    if (!clusters) return undefined
    return clusters
      .filter((c) =>
        (!filterModality || c.inputModality === filterModality) &&
        (!filterUseCase || c.useCase === filterUseCase) &&
        eligibilityCount(c) > 0
      )
      .sort((a, b) => eligibilityCount(b) - eligibilityCount(a))
  }, [clusters, filterModality, filterUseCase, eligibility])

  if (!isAdmin) {
    return <div className="p-6 text-sm text-neutral-500">Admin access required</div>
  }

  const totalFiltered = filtered?.reduce((s, c) => s + eligibilityCount(c), 0) ?? 0

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold text-neutral-900">Project Clusters</h1>
          <p className="text-sm text-neutral-500 mt-0.5">
            {stats ? (
              <>
                <span className="tabular-nums">{stats.totalClassified}</span> classified
                {stats.withClips !== undefined && (
                  <> · <span className="tabular-nums">{stats.withClips}</span> with clips</>
                )}
                {stats.withExports !== undefined && (
                  <> · <span className="tabular-nums">{stats.withExports}</span> with exports</>
                )}
                {' · '}
                <span className="tabular-nums">{stats.totalClusters}</span> clusters
              </>
            ) : 'Loading...'}
            {' · '}daily 11:00 UTC
          </p>
        </div>

        {/* Eligibility toggle */}
        <div className="inline-flex rounded-lg border border-neutral-200 bg-neutral-50 p-0.5 text-xs shrink-0">
          {([
            { key: 'all', label: 'All', count: stats?.totalClassified },
            { key: 'clips', label: 'With clips', count: stats?.withClips },
            { key: 'exports', label: 'With exports', count: stats?.withExports },
          ] as const).map((opt) => {
            const active = eligibility === opt.key
            return (
              <button
                key={opt.key}
                onClick={() => setEligibility(opt.key)}
                className={cn(
                  'px-2.5 py-1 rounded-md transition-colors font-medium flex items-center gap-1.5',
                  active ? 'bg-white text-neutral-900 shadow-sm' : 'text-neutral-500 hover:text-neutral-700',
                )}
              >
                {opt.label}
                {opt.count !== undefined && (
                  <span className={cn('tabular-nums text-[10px]', active ? 'text-neutral-500' : 'text-neutral-400')}>
                    {opt.count}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </div>

      {/* Filter bar */}
      {clusters && clusters.length > 0 && (
        <div className="space-y-2">
          {/* Modality filters */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-semibold text-neutral-400 uppercase tracking-wider w-16 shrink-0">Input</span>
            {uniqueModalities.map((mod) => {
              const count = clusters.filter((c) => c.inputModality === mod).reduce((s, c) => s + eligibilityCount(c), 0)
              if (count === 0) return null
              const isActive = filterModality === mod
              return (
                <button
                  key={mod}
                  onClick={() => setFilterModality(isActive ? null : mod)}
                  className={cn(
                    'inline-flex items-center gap-1 px-2 py-1 rounded border text-xs font-medium transition-colors',
                    modalityClasses(mod, isActive),
                  )}
                >
                  {mod} <span className="text-[10px] opacity-70">({count})</span>
                </button>
              )
            })}
          </div>
          {/* Use case filters */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-semibold text-neutral-400 uppercase tracking-wider w-16 shrink-0">Market</span>
            {uniqueUseCases.map((uc) => {
              const count = clusters.filter((c) => c.useCase === uc).reduce((s, c) => s + eligibilityCount(c), 0)
              if (count === 0) return null
              const isActive = filterUseCase === uc
              return (
                <button
                  key={uc}
                  onClick={() => setFilterUseCase(isActive ? null : uc)}
                  className={cn(
                    'inline-flex items-center gap-1 px-2 py-1 rounded border text-xs font-medium transition-colors',
                    isActive
                      ? 'bg-neutral-800 text-white border-neutral-800'
                      : 'border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50',
                  )}
                >
                  <span className={cn('w-2 h-2 rounded-full', useCaseColor(uc))} />
                  {uc} <span className="text-[10px] opacity-60">({count})</span>
                </button>
              )
            })}
          </div>
          {(filterModality || filterUseCase) && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-neutral-500">{filtered?.length} clusters · {totalFiltered} projects</span>
              <button
                onClick={() => { setFilterModality(null); setFilterUseCase(null) }}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>
      )}

      {/* Cluster cards */}
      <section>
        {filtered === undefined ? (
          <div className="px-4 py-12 text-sm text-neutral-400 flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading clusters…
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <Grid3X3 className="h-8 w-8 text-neutral-300 mx-auto mb-2" />
            <p className="text-sm text-neutral-500">{clusters?.length ? 'No clusters match filters.' : 'No clusters yet.'}</p>
            {!clusters?.length && (
              <p className="text-xs text-neutral-400 mt-1">
                Run <code className="bg-neutral-100 px-1 rounded">bun scripts/cluster-projects.ts run</code> to classify projects.
              </p>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {filtered.map((cluster) => {
              const isExpanded = expandedId === cluster._id
              const isEditing = editingId === cluster._id

              return (
                <div key={cluster._id} className="rounded-lg border border-neutral-200 bg-white overflow-hidden">
                  {/* Cluster card header */}
                  <div
                    className="px-4 py-3 cursor-pointer hover:bg-neutral-50/50 transition-colors"
                    onClick={() => setExpandedId(isExpanded ? null : cluster._id)}
                  >
                    <div className="flex items-start gap-4">
                      {/* Thumbnail strip */}
                      {cluster.thumbnails.length > 0 && (
                        <div className="flex gap-0.5 shrink-0">
                          {cluster.thumbnails.slice(0, 4).map((url: string, i: number) => (
                            <img
                              key={i}
                              src={url}
                              className="w-10 h-10 rounded object-cover bg-neutral-100"
                              loading="lazy"
                            />
                          ))}
                        </div>
                      )}

                      {/* Main info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-neutral-900">{cluster.name}</span>
                          <span className="text-sm font-semibold tabular-nums text-neutral-900 bg-neutral-100 px-1.5 py-0.5 rounded">
                            {eligibilityCount(cluster)}
                          </span>
                          <div className="flex items-center gap-1.5">
                            <span className={cn('px-1.5 py-0.5 rounded border text-[10px] font-medium uppercase tracking-wider', modalityClasses(cluster.inputModality, false))}>
                              {cluster.inputModality}
                            </span>
                            <span className="text-neutral-300">/</span>
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider bg-neutral-100 text-neutral-700">
                              <span className={cn('w-1.5 h-1.5 rounded-full', useCaseColor(cluster.useCase))} />
                              {cluster.useCase}
                            </span>
                          </div>
                          {eligibility === 'all' && ((cluster.withClips ?? 0) > 0 || (cluster.withExports ?? 0) > 0) && (
                            <span className="text-[10px] text-neutral-400 tabular-nums">
                              {cluster.withClips ?? 0} clipped · {cluster.withExports ?? 0} exported
                            </span>
                          )}
                        </div>

                        <p className="text-xs text-neutral-500 mt-1 line-clamp-2">
                          {cluster.detailedDescription || cluster.description}
                        </p>

                        {/* Skills heatmap inline */}
                        {(Object.keys(cluster.skillCounts).length > 0 || Object.keys(cluster.featureCounts).length > 0) && (
                          <div className="mt-1.5">
                            <SkillHeatmap skills={cluster.skillCounts} features={cluster.featureCounts} />
                          </div>
                        )}
                      </div>

                      {/* Right side: sparkline + chevron */}
                      <div className="flex items-center gap-3 shrink-0">
                        <div className="flex flex-col items-end gap-0.5">
                          <Sparkline data={cluster.sparkline} color="#6366f1" />
                          <span className="text-[9px] text-neutral-400">8 weeks</span>
                        </div>
                        <ChevronDown className={cn('h-4 w-4 text-neutral-400 transition-transform', isExpanded && 'rotate-180')} />
                      </div>
                    </div>
                  </div>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <ClusterDetail
                      clusterId={cluster._id}
                      cluster={cluster}
                      eligibility={eligibility}
                      isEditing={isEditing}
                      editDesc={editDesc}
                      onEditStart={() => {
                        setEditingId(cluster._id)
                        setEditDesc(cluster.detailedDescription || cluster.description)
                      }}
                      onEditCancel={() => setEditingId(null)}
                      onEditSave={async () => {
                        await updateCluster({
                          clusterId: cluster._id,
                          detailedDescription: editDesc,
                        })
                        setEditingId(null)
                      }}
                      onEditChange={setEditDesc}
                    />
                  )}
                </div>
              )
            })}
          </div>
        )}
      </section>

      {/* Recent runs */}
      {runs && runs.length > 0 && (
        <section>
          <h2 className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-2">Recent Runs</h2>
          <div className="rounded-lg border border-neutral-200 bg-white divide-y divide-neutral-100">
            {runs.map((run) => (
              <div key={run._id} className="px-4 py-2 flex items-center gap-3 text-xs">
                <span className={cn(
                  'px-1.5 py-0.5 rounded font-medium',
                  run.status === 'completed' ? 'bg-green-50 text-green-700' :
                  run.status === 'failed' ? 'bg-red-50 text-red-700' :
                  'bg-blue-50 text-blue-700'
                )}>
                  {run.status}
                </span>
                <span className="text-neutral-600 tabular-nums">{run.projectsProcessed} projects</span>
                <span className="text-neutral-400">·</span>
                <span className="text-neutral-600 tabular-nums">+{run.clustersCreated} clusters</span>
                <span className="flex-1" />
                <span className="text-neutral-400">{formatTimeAgo(run.startedAt)}</span>
                {run.error && (
                  <span className="text-red-500 truncate max-w-[200px]" title={run.error}>{run.error}</span>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}

function ClusterDetail({
  clusterId,
  cluster,
  eligibility,
  isEditing,
  editDesc,
  onEditStart,
  onEditCancel,
  onEditSave,
  onEditChange,
}: {
  clusterId: Id<'projectClusters'>
  cluster: any
  eligibility: 'all' | 'clips' | 'exports'
  isEditing: boolean
  editDesc: string
  onEditStart: () => void
  onEditCancel: () => void
  onEditSave: () => void
  onEditChange: (v: string) => void
}) {
  const detail = useQuery(api.clustering.getClusterDetail, { clusterId })

  const visibleProjects = detail?.projects.filter((p: any) =>
    eligibility === 'clips' ? (p.generatedClipCount ?? 0) > 0
    : eligibility === 'exports' ? (p.exportCount ?? 0) > 0
    : true
  ) ?? []

  return (
    <div className="border-t border-neutral-100 px-4 pb-4 space-y-4 bg-neutral-50/30">
      {/* Editable description */}
      <div className="pt-3">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[10px] font-semibold text-neutral-400 uppercase tracking-wider">Description</span>
          {!isEditing && (
            <button onClick={onEditStart} className="text-neutral-400 hover:text-neutral-600">
              <Pencil className="h-3 w-3" />
            </button>
          )}
        </div>
        {isEditing ? (
          <div className="flex gap-2">
            <textarea
              value={editDesc}
              onChange={(e) => onEditChange(e.target.value)}
              className="flex-1 text-xs px-2 py-1.5 rounded border border-neutral-300 bg-white text-neutral-800 resize-none focus:outline-none focus:border-blue-500"
              rows={3}
            />
            <div className="flex flex-col gap-1">
              <button onClick={onEditSave} className="p-1 rounded hover:bg-green-100 text-green-600"><Check className="h-3.5 w-3.5" /></button>
              <button onClick={onEditCancel} className="p-1 rounded hover:bg-red-100 text-red-400"><X className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ) : (
          <p className="text-xs text-neutral-600 leading-relaxed">
            {cluster.detailedDescription || cluster.description}
          </p>
        )}
      </div>

      {/* Common patterns */}
      {cluster.commonPatterns && cluster.commonPatterns.length > 0 && (
        <div>
          <span className="text-[10px] font-semibold text-neutral-400 uppercase tracking-wider block mb-1">Common Patterns</span>
          <div className="flex flex-wrap gap-1">
            {cluster.commonPatterns.map((p: string, i: number) => (
              <span key={i} className="text-[11px] px-1.5 py-0.5 rounded bg-white border border-neutral-200 text-neutral-600">
                {p}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Projects grid */}
      {detail === undefined ? (
        <div className="flex items-center gap-2 text-xs text-neutral-400 py-4">
          <Loader2 className="h-3 w-3 animate-spin" /> Loading projects…
        </div>
      ) : detail && visibleProjects.length > 0 ? (
        <div>
          <span className="text-[10px] font-semibold text-neutral-400 uppercase tracking-wider block mb-2">
            Projects ({visibleProjects.length}{visibleProjects.length !== detail.totalAssignments && ` of ${detail.totalAssignments}`})
          </span>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {visibleProjects.map((p: any) => (
              <Link
                key={p._id}
                to={`/video/${p.shortcode || p._id}`}
                className="rounded-lg border border-neutral-200 bg-white hover:border-neutral-300 transition-colors overflow-hidden group"
              >
                {/* Thumbnail */}
                {p.thumbnail ? (
                  <div className="relative aspect-video bg-neutral-100">
                    <img src={p.thumbnail} className="w-full h-full object-cover" loading="lazy" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                  </div>
                ) : (
                  <div className="aspect-video bg-neutral-100 flex items-center justify-center">
                    <Grid3X3 className="h-6 w-6 text-neutral-300" />
                  </div>
                )}
                {/* Info */}
                <div className="p-2.5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-neutral-900 truncate">
                        {p.title || p.narrative?.slice(0, 60) || '(untitled)'}
                      </p>
                      <p className="text-[10px] text-neutral-400 mt-0.5">{p.shortcode}</p>
                    </div>
                    <span className={cn(
                      'text-[10px] tabular-nums px-1 py-0.5 rounded shrink-0',
                      p.confidence >= 0.8 ? 'bg-green-50 text-green-700' : p.confidence >= 0.5 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-600'
                    )}>
                      {Math.round(p.confidence * 100)}%
                    </span>
                  </div>
                  {/* Meta row */}
                  <div className="flex items-center gap-2 mt-1.5 text-[10px] text-neutral-400">
                    <span>{p.clipCount} clips</span>
                    {(p.generatedClipCount ?? 0) > 0 && (
                      <span className="text-emerald-600 font-medium">✓ {p.generatedClipCount} generated</span>
                    )}
                    {(p.exportCount ?? 0) > 0 && (
                      <span className="text-indigo-600 font-medium">⇪ exported</span>
                    )}
                  </div>
                  {p.generationModes.length > 0 && (
                    <div className="text-[10px] text-neutral-400 mt-0.5">
                      {p.generationModes.map((m: string) => SKILL_LABELS[m] || m).join(', ')}
                    </div>
                  )}
                  <div className="flex items-center gap-1.5 mt-1 text-[10px] text-neutral-400">
                    {p.hasCharacters && <Users className="h-3 w-3" />}
                    {p.hasVoiceover && <Mic className="h-3 w-3" />}
                    <span className="flex-1" />
                    <span>{p.userEmail}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  )
}
